"use client";
import { Body, Heading } from "@/client/foundations/Typography";
// import { Box, Space, Stack } from "@fastlooptech/layout-components";
import { useResponsive } from "@/client/hooks/useResponsive";
import { ButtonWrapper } from "@/components/ButtonWrapper";
import { Icons } from "@/components/Icons";
import { ImageType, T_KeyValue } from "@/types/app.type";
import React, { ReactElement, useEffect, useMemo, useState } from "react";
import dynamic from 'next/dynamic';
// import { CardCarousel } from "./CardCarousel";
import { CardVideo } from "./CardVideo";
import IconSet from "@/components/Icons/IconSet";
import classNames from "classnames";
import { inputToNumber } from "@/helpers/utils.helper";
import { sendEventToGTM } from "@/helpers/gtm.helper";
import Spinner from "@/components/Spinner";
// import ImageWrapper from "@/components/ImageWrapper";

const ImageWrapper = dynamic(() => import("@/components/ImageWrapper"), {
	ssr: false, // optional: disable SSR if the component depends on `window`
	loading: () => <Spinner loading={true}><></></Spinner>
});
const CardCarousel = dynamic(() => import("./CardCarousel"), {
	ssr: false, // optional: disable SSR if the component depends on `window`
	loading: () => <Spinner loading={true}><></></Spinner>
});

export interface CardProps {
	"data-test-id"?: string;
	/**
	 * The Card id.
	 * @type string
	 */
	id?: string;
	/**
	 * The Card custom style.
	 * @type {T_KeyValue}
	 */
	styles?: React.CSSProperties;
	/**
	 * The Card className.
	 * @type {string}
	 */
	className?: string;
	/**
	 * Children is body of the Card.
	 * @type ReactElement
	 * @required
	 */
	children?: ReactElement;
	/**
	 * The title of the Card.
	 * @type string
	 * @required
	 */
	title?: string;
	/**
	 * The subtitle of the Card.
	 * @type string
	 * @optional
	 */
	subtitle?: string;
	/**
	 * The description of the Card.
	 * @type string
	 * @optional
	 */
	description?: any;
	/**
	 * Sub title font size.
	 * @type string
	 * @optional
	 */
	subtitleFontSize?: 's' | 'm' | 'l' | 'xl';
	/**
	 * Title font size.
	 * @type string
	 * @optional
	 */
	titleFontSize?: 's' | 'm' | 'l' | 'xl';
	/**
	 * Body font size.
	 * @type string
	 * @optional
	 */
	bodyFontSize?: 'xxs' | 'xs' | 's' | 'm' | 'l' | 'xl';
	/**
	 * Hide buttons of the Card.
	 * @type boolean
	 */
	hideButtons?: boolean;
	/**
	 * Enable animation triggered whe the element is visible.
	 * @type boolean
	 * @optional
	 */
	animated?: boolean;
	/**
	 * The animation type triggered whe the element is visible. Default is fadeIn
	 * @type string
	 * @optional
	 */
	animation?: string;
	/**
	 * The action of the Card.
	 * @type () => void
	 * @optional
	 */
	// action?: () => void;
	/**
	 * Button 1 Label of the Card.
	 * @type string
	 * @optional
	 */
	btn1_label?: string;
	/**
	 * Button 1 Icon in the Card.
	 * @type any
	 * @optional
	 */
	btn1_icon?: any;
	/**
	 * Button 1 Icon Position
	 * @type string
	 * @optional
	 */
	btn1_iconPosition?: 'hide' | 'top' | 'right' | 'left';
	/**
	 * Button 1 Variant.
	 * @type string
	 * @optional
	 */
	btn1_variant?: string;
	/**
	 * Button 1 size.
	 * @type string
	 * @optional
	 */
	btn1_size?: string;
	/**
	 * The action of the Card.
	 * @type () => void
	 * @optional
	 */
	btn1_action?: () => void;
	/**
	 * Switch between different image positions or hide the image
	 * @type string
	 * @optional
	 */
	buttonUrl?: string;
	linkTargetBlank?: boolean;
	hideText?: boolean;
	// Media:
	/**
	 * Hide media of the Card.
	 * @type boolean
	 */
	hideMedia?: boolean;
	/**
	 * The images source the Card.
	 * @type ImageType[]
	 * @optional
	 */
	image?: ImageType;
	/**
	 * The images source the Card.
	 * @type ImageType[]
	 * @optional
	 */
	images?: ImageType[];
	/**
	 * The video source of the Card.
	 * @type string
	 * @optional
	 */
	video?: string;
	mobileVideo?: any,
	/**
	 * Color of the background behind media e.g. when image is smaller than it's wrapper
	 * @type string
	 * @optional
	 */
	cardBackgroundColor?: string;
	contentBackgroundColor?: string;
	mediaPosition?: "Normal" | "LeftEdge";
	mediaWidth?: string; //number;
	mediaHeight?: string; //number;
	mediaWrapperWidth?: string;
	mediaWrapperHeight?: string;
	mediaStartViewX?: string;
	mediaStartViewY?: string;
	centerTextVertically?: boolean;
	eventName?: string;
	playButtonPosition?: 'none' | 'center' | 'bottom-left' | 'top-right';
	pauseable?: boolean;
	autoPlay?: boolean;
	maxWidth?: string;
	mediaOverlay?: ReactElement;
	roundedCorners?: boolean;
	thumbnailOnly?: boolean;
	contentMinHeight?: string;
	contentMaxHeight?: string;
	showInSection?: boolean;
	sectionId?: string;
}

export default function Card({
	hideText = false,
	centerTextVertically = false,
	hideButtons = false,
	btn1_label = "Show details",
	btn1_icon = 'Placeholder',
	btn1_iconPosition = "left",
	btn1_variant = "primary",
	btn1_size = "s",
	btn1_action = () => { },
	buttonUrl,
	linkTargetBlank = false,
	// Image:
	image = { width: 0, height: 0, src: "" },
	mediaWidth = "30px",
	mediaHeight = "30px",
	mediaPosition = "Normal",
	mediaStartViewX = '0px',
	mediaStartViewY = '0px',
	mediaWrapperWidth = '56px',
	mediaWrapperHeight = '56px',
	cardBackgroundColor,
	contentBackgroundColor = "#FFFFFF00",
	eventName,
	playButtonPosition,
	pauseable = false,
	autoPlay = false,
	maxWidth = "100%",
	mediaOverlay,
	roundedCorners = false,
	thumbnailOnly = false,
	contentMinHeight = "30px",
	contentMaxHeight = "auto",
	showInSection = false,
	sectionId = "",
	...props
}: CardProps) {
	const { isMd } = useResponsive();
	const buttons = [
		{
			label: btn1_label,
			icon: btn1_icon,
			iconPosition: btn1_iconPosition,
			variant: btn1_variant,
			size: btn1_size,
			action: btn1_action,
		},
	];

	const [sectionVisible, setSectionVisible] = useState(false);
	const isInContentful = (typeof window !== "undefined") && window.self !== window.top;

	useEffect(()=>{
		setSectionVisible(false);
	}, [])

	const CardMedia = useMemo(() => {
		if (props?.images) {
			return (
				<CardCarousel
					data-test-id="card-carousel"
					images={props?.images}
					style={{ width: image?.width || "0px", height: image?.height || "0px" }}
				// style={{width: mediaWrapperWidth || mediaWidth || "100%", height: mediaWrapperHeight || mediaHeight || "auto"}}
				/>
			);
		}

		// A component wrapping the media to be able to change position in frame
		const MediaWrapper = ({ children }: { children: React.ReactNode }) => (
			<div
				// className="relative1 overflow-hidden shrink-0"
				className={classNames("relative overflow-hidden shrink-0"
					// {
					// 	'rounded-l-image-card': roundedCorners && mediaPosition === 'LeftEdge',
					// 	'rounded-t-image-card': roundedCorners && mediaPosition === 'Normal',
					// 	'rounded-image-card': roundedCorners && hideText,
					// }
				)}
				style={{
					width: mediaPosition === 'LeftEdge' ? (mediaWrapperWidth || `${image.width}px`) : '100%',
					maxHeight: mediaWrapperHeight || `${image.height}px`,
					// width: (mediaPosition === 'LeftEdge' ? (mediaWrapperWidth || mediaWidth || `${image.height}px` || "100%") : "100%"), 
					height: mediaWrapperHeight || mediaHeight || `${image.height}px` || "auto",
					// backgroundColor: mediaBackgroundColor,
				}}>
				{/* {mediaWrapperWidth} */}
				{children}
			</div>
		);

		return (
			<MediaWrapper>
				{/* Img ---- {mediaWidth} ---- {mediaHeight} */}

				{/* ------- For video resources ------ */}
				{props?.video ? (
					<>
						{/* <Video
							className={`relative max-w-none [&>video]:object-cover`}
							style={{
								width: `${mediaWidth}`,
								height: `${mediaHeight}`,
								left: mediaStartViewX.startsWith('-') ? mediaStartViewX.slice(1) : `-${mediaStartViewX}`,
								top: mediaStartViewY.startsWith('-') ? mediaStartViewY.slice(1) :  `-${mediaStartViewY}`,
							}}
							data-test-id={props["data-test-id"]}
							minHeight={minHeight || "100px"}
							width={props?.style?.width as string}
							height={props?.style?.height as string}
							videoUrl={isMd ? props?.video : props?.mobileVideo}
							mobileVideo={props?.mobileVideo}
							playButtonPosition={playButtonPosition}
							loop
							muted
							// playsinline
							pauseable={pauseable || false}
							autoPlay={autoPlay}
							eventName={eventName}
							showCustomPlayButton={true}
							thumbnailOnly={thumbnailOnly || false}
							fallback={
								<ImageWrapper
									src={image?.src}
									alt="card-image"
									placeholder="blur"
									blurDataURL={image?.src}
									width={image?.width || 50}
									height={image?.height || 50}
									priority
									// loading="lazy"
									loader={({ src }) => src}
									// style={props?.style}
									style={{
										width: `${mediaWidth}`,
										height: `${mediaHeight}`,
										left: mediaStartViewX.startsWith('-') ? mediaStartViewX.slice(1) : `-${mediaStartViewX}`,
										top: mediaStartViewY.startsWith('-') ? mediaStartViewY.slice(1) :  `-${mediaStartViewY}`,
									}}
									className="!h-full object-cover max-w-none"
								/>
							}
						posterImage={image?.src} /> */}

						<CardVideo
							data-test-id="card-video"
							url={props?.video}
							mobileVideo={props?.mobileVideo}
							fallback={image}
							image={image}
							animated={props?.animated ?? false}
							animation={props?.animation ?? ""}
							// className={`absolute max-w-none [&>video]:object-cover`}
							className={`relative max-w-none [&>video]:object-cover`}
							thumbnailOnly={thumbnailOnly}
							style={{
								width: `${mediaWidth}`,
								height: `${mediaHeight}`,
								left: mediaStartViewX.startsWith('-') ? mediaStartViewX.slice(1) : `-${mediaStartViewX}`,
								top: mediaStartViewY.startsWith('-') ? mediaStartViewY.slice(1) : `-${mediaStartViewY}`,
							}}
							playButtonPosition={playButtonPosition}
							pauseable={pauseable || false}
							autoPlay={autoPlay}
							eventName={eventName}
							showCustomPlayButton={true} // todo (maybe use as variable)
						/>
					</>
				) : (
					<>
						{/* ------- For image resources ------ */}
						<ImageWrapper
							src={image?.src}
							// width={mediaWidth || image?.width}
							// height={mediaHeight || image?.height} // not important but required to omit  build errors
							width={inputToNumber(mediaWidth) || image?.width}
							height={inputToNumber(mediaHeight) || image?.height} // not important but required to omit  build errors
							alt="card-image"
							placeholder="blur"
							blurDataURL={image?.src}
							// loading="lazy"
							loader={({ src }) => src}
							className="absolute object-cover max-w-none"
							style={{
								width: mediaWidth,
								height: mediaHeight,
								left: mediaStartViewX.startsWith('-') ? mediaStartViewX.slice(1) : `-${mediaStartViewX}`,
								top: mediaStartViewY.startsWith('-') ? mediaStartViewY.slice(1) : `-${mediaStartViewY}`,
							}}
							data-aos={props?.animated ? props?.animation ?? "fade-in" : ""}
						/>
					</>
				)}
			</MediaWrapper>
		);
	}, [
		image,
		props,
		mediaStartViewX,
		mediaStartViewY,
		mediaWrapperWidth,
		mediaWrapperHeight,
		// mediaBackgroundColor,
		mediaWidth,
		mediaHeight,
		playButtonPosition,
		autoPlay,
		mediaPosition,
		pauseable,
		eventName,
		thumbnailOnly
	]);

	const onAction = () => {
		// Send GTM event for card clicks
		const eventType = props?.video ? "Video Card Clicked" : "Image Card Clicked";

		sendEventToGTM({
			event: eventType,
			component: "Card",
			componentId: props?.id || "cd",
			cardTitle: props?.title || '',
			cardSubtitle: props?.subtitle || '',
			cardDescription: props?.description || '',
			pagePath: typeof window !== 'undefined' ? window?.location?.pathname : '',
			eventName: eventName || 'NA'
		});

		if (window && eventName) {
			window.dispatchEvent(new Event(eventName));
		}
	}

	const checkSectionVisible = () => {
		if (showInSection) {
			const section = document?.getElementById(sectionId);
			if (section) {
				const observer = new IntersectionObserver(entries => {
					entries?.forEach(e => {
						if (e?.isIntersecting) {
							setSectionVisible(true)
						}
						else {
							setSectionVisible(false)
						}
					})
				}, { rootMargin: "-200px 0px -100px 0px" })
				observer.observe(section)
			}
		}
	}

	checkSectionVisible();

	return (
		(isInContentful ? true : (showInSection ? sectionVisible : true)) && <div
			id={props?.id || "cd"}
			data-test-id={props["data-test-id"]}
			className={classNames(
				props?.className,
				"flex relative",
				"overlow-hidden shrink-0",
				`${eventName ? "cursor-pointer" : ""}`,
				{
					"flex-col": mediaPosition !== "LeftEdge",
					"items-center": mediaPosition === "LeftEdge",
					'rounded-r-image-card': roundedCorners && mediaPosition === 'LeftEdge',
					'rounded-b-image-card': roundedCorners && mediaPosition === 'Normal',
					'rounded-image-card': roundedCorners && props.hideMedia,
				}
			)}
			onClick={onAction}
			{...(() => { return eventName ? { title: "Click here to open it." } : {} })()}
			// style={{maxWidth, width: mediaWrapperWidth || mediaWidth || "100%", minHeight: mediaWrapperHeight || mediaHeight || "auto", ...props?.styles}}
			style={{ maxWidth, ...props?.styles }}
		>
			{/* <pre>{JSON.stringify(sectionId, null, 2)}</pre> */}

			{props?.children ? (
				<>
					{/* ------------------------------------------------------------------------------------------
					------------ If card is used only as a wrapper/container for provided children -----------
					------------------------------------------------------------------------------------------ */}
					{props?.children}
				</>
			) : (
				<>
					{/* ---------------------------------------------------------------------------------------------
					------------ If card is used with props (title, subtitle, hideMedia, video, ... ) -----------
					--------------------------------------------------------------------------------------------- */}

					{/* ------- Show/Hide media section ------- */}
					{!props.hideMedia && CardMedia}

					{/* ------- Show/Hide text content based on props value (if props cantain value then show otherwish hide) ------- */}
					{!hideText && (
						<div className={classNames(
							// "relative",
							"px-cardX",
							"flex flex-col items-start",
							{
								"md:py-cardY gap-card-text-gap": mediaPosition !== 'LeftEdge',
								"py-[5px]": mediaPosition === 'LeftEdge',
								"py-[10px]": props?.video && mediaPosition !== 'LeftEdge',
								"py-cardY": !props?.video && mediaPosition !== 'LeftEdge',
								"my-auto": centerTextVertically,
							},
						)}>
							<div className={classNames(
								"flex flex-col items-start gap-card-text-gap",
								"top-panel",
								`bg-[${contentBackgroundColor}]`,
							)}
								style={{ minHeight: contentMinHeight, maxHeight: contentMaxHeight }}
							>
								{/* <>{contentMinHeight}</> */}

								{/* ------ Title/Main Heading ------ */}
								{props?.title && (
									<Heading
										size={props?.subtitleFontSize || "m"}
										animated={props?.animated ?? false}
										className={classNames('!font-image-card-header')}
										animation={props?.animation ?? ""}>
										{props?.title || ""}
									</Heading>
								)}
								{/* ------ Subtitle/Subheading ------ */}
								{props?.subtitle && (
									<Heading
										size={props?.titleFontSize || "s"}
										className={classNames('!font-image-card-sub-header')}
										animated={props?.animated ?? false}
										animation={props?.animation ?? ""}>
										{props?.subtitle || ""}
									</Heading>
								)}

								{/* ------ Description ----- */}
								{props?.description && (
									<>
										{/* {props?.description} */}
										{/* <ContentfulRichText document={props?.description} /> */}
										{/* {JSON.stringify(props?.description)} */}
										<Body
											size={props?.bodyFontSize || "l"}
											animated={props?.animated ?? false}
											animation={props?.animation ?? ""}
										>
											{props?.description}
										</Body>
									</>
								)}
							</div>
							<div className={"bottom-panel"}>
								{/* ------ Action buttons / CTAs ------ */}
								{!hideButtons &&
									buttons &&
									buttons?.map((btn: any) => {
										const buttonIconElement = React.createElement(
											Icons[btn.icon as keyof typeof IconSet],
											{
												size: "m",
												id: btn.label + btn.icon,
												key: btn.label + btn.icon + (new Date()).getTime()
											},
										);

										return (
											<ButtonWrapper
												key={`ButtonWrapper--card--${btn?.id}-${(new Date()).getTime()}`}
												id="cta-details"
												variant={btn?.variant}
												size={btn?.size}
												action={btn?.action}
												{...(() => {
													return btn?.icon
														? btn?.iconPosition === "after"
															? { afterIcon: btn?.icon }
															: { beforeIcon: btn?.icon }
														: {};
												})()}
												className="flex gap-0.5 items-center"
												url={buttonUrl} // href for link, does nothing when falsy
												value={
													<>
														{btn.iconPosition !== "hide" && btn.iconPosition === "left" &&
															buttonIconElement}
														{btn?.label}
														{/* -- {btn.variant} --- {btn1_variant} */}
														{btn.iconPosition !== "hide" && btn.iconPosition === "right" &&
															buttonIconElement}
													</>
												} // children
												targetBlank={linkTargetBlank}
												location={props?.video ? "Video Card" : (image?.src ? "Image Card" : "")}
												GTMEnabled={true}
												GTMPayload={{
													label: btn?.label,
													action: btn?.action
												}}
											/>
										);
									})
								}
							</div>
						</div>
					)}
				</>
			)}
		</div>
	);
}
